import React from 'react';
import get from 'lodash/get';
import { Field, FieldProps } from '@atlaskit/form';
import Select, { OptionProps } from '@atlaskit/select';
import { N0 } from '@atlaskit/theme/colors';
import Badge from '@atlaskit/badge';
import Lozenge from '@atlaskit/lozenge';
import defaultTo from 'lodash/defaultTo';
import startCase from 'lodash/startCase';
import {
  InvoiceGroup,
  CommerceAddress,
  PaymentMethod,
  PaymentOnTerms,
  CreditCard,
  generatedTypesNew,
} from '@css-xp/graphtypes';
import BillingAddressDetailsLine from './billing-address-details-line';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { COMMERCIAL_PRICE_ELIGIBILITY } from '@css-xp/ppc-constants';
import { ENABLE_CREDIT_MANAGEMENT } from '@atlassian/cssxp-utils-generic';
import { Box, Flex, Text } from '@atlaskit/primitives';
import {
  BillingFrequency,
  EntitlementTermDetails,
  Maybe,
  PaymentMethodType,
  EndCustomerContact,
} from '@css-xp/graphtypes/src/generated-types-new';
import { useFeatureToggle } from '../../hooks';
import { CustomPanel } from '../../custom-panel';
import { PaymentMethodSelectDetailsLine } from '../../common-tabs';
import { NotAvailableBillingProfiles } from './billing-profile-form-field.styles';
import { SwitchBillingProfileCards } from './switch-billing-profile-cards';

export interface BillingProfileSelectItem {
  value: string; // invoiceGroupId
  invoiceGroup: InvoiceGroup;
  isDisabled: boolean;
  disabledMessage: string;
  selectedCurrency: string;
  primaryCustomerContact?: {
    isLoading?: boolean;
    data?: EndCustomerContact;
  };
}

const formatAddressLabel = (node: CommerceAddress) => {
  let formatAddressLabel = '';
  if (node?.name) {
    formatAddressLabel += node.name;
  }
  if (node?.addressLine1) {
    formatAddressLabel += `, ${node.addressLine1}`;
  }
  if (node?.addressLine2) {
    formatAddressLabel += `, ${node.addressLine2}`;
  }
  if (node?.postcode) {
    formatAddressLabel += `, ${node.postcode}`;
  }
  if (node?.city) {
    formatAddressLabel += `, ${node.city}`;
  }
  if (node?.state) {
    formatAddressLabel += `, ${node.state}`;
  }
  if (node?.country) {
    formatAddressLabel += `, ${node.country}`;
  }
  return formatAddressLabel;
};

const formatCreditCardLabel = (paymentMethod: PaymentMethod) => {
  switch (paymentMethod?.type as any) {
    case PaymentMethodType.CARD: {
      const creditCard = paymentMethod as CreditCard;
      return `Credit card: **** ${creditCard?.card?.last4Numbers} (${creditCard?.card?.brand?.toUpperCase()}), exp - ${
        creditCard?.card?.expirationMonth
      }/${creditCard?.card?.expirationYear}`;
    }
    case PaymentMethodType.DEFERRED: {
      const paymentOnTerms = paymentMethod as PaymentOnTerms;
      return `Payment on terms: due in ${paymentOnTerms.terms?.invoiceDueDays} days`;
    }
    case generatedTypesNew.PaymentMethodTypeV2.PAYPAL: {
      return `PayPal: ${(paymentMethod as any)?.payPal?.email}`;
    }
    default:
      return `Unknown payment method`;
  }
};

const BillingProfileSelectOption = ({
  data,
  getStyles,
  innerProps,
  innerRef,
  ...props
}: OptionProps<BillingProfileSelectItem>) => {
  const { invoiceGroup, creditDetails, disabledMessage } = data as {
    invoiceGroup: InvoiceGroup;
    creditDetails: {
      totalCreditLimit: number;
      availableCreditLimit: number;
    };
    disabledMessage: string;
  };
  const isNameNone = invoiceGroup.name && invoiceGroup.name?.toLowerCase() !== 'none';
  const optionStyles = getStyles('option', props);
  return (
    <section ref={innerRef} {...innerProps} style={optionStyles} key={`select-option-${invoiceGroup.id}`}>
      <span data-testid={`select-option-${invoiceGroup.id}`}>
        <span className="billing-profile-name-section">
          <strong>
            {isNameNone ? invoiceGroup.name : 'Unnamed invoice group'} ({invoiceGroup.currency})
          </strong>
          {disabledMessage ? (
            <span className="ml-8" data-testid={'not-available-lozenge'}>
              <Lozenge appearance="removed">Not available</Lozenge>
            </span>
          ) : null}
        </span>
        <BillingAddressDetailsLine address={invoiceGroup?.shipToParty} noAddressLabel="No shipping address" />
        <div>
          Price eligibility:{' '}
          {startCase(defaultTo(get(invoiceGroup, 'shipToPriceEligibility'), COMMERCIAL_PRICE_ELIGIBILITY))}
        </div>
        <PaymentMethodSelectDetailsLine
          paymentMethod={invoiceGroup.defaultPaymentMethod}
          creditDetails={creditDetails}
        />
        {disabledMessage && (
          <div className="billing-profile-disabled-message" data-testid={'disable-error-message'}>
            {disabledMessage}
          </div>
        )}
      </span>
    </section>
  );
};

interface Props {
  fieldName?: string;
  fieldLabel?: string;
  isRequired?: boolean;
  billingProfiles?: InvoiceGroup[] | null;
  entitlementBillingInvoiceGroupId?: string | null;
  scheduledBillingInvoiceGroupId?: string | null;
  isDisabled?: boolean | false;
  setSelectedInvoiceGroupPricingPlanValue?: Function | null;
  productId?: string | undefined;
  className?: string | undefined;
  // specifically shows the invoice groups which are related to term payment method
  // By default it's value is true so that it can impact only when passed as false from parent
  showTermPaymentMethodGroup?: boolean | undefined | null;
  onChange?: (selectedItem: BillingProfileSelectItem) => void;
  selected?: InvoiceGroup | null;
  validate?: (selectedItem: BillingProfileSelectItem) => boolean;
  billingFrequency?: Maybe<string> | undefined;
  endTimeStamp?: number | null | undefined;
  creditDetails?: EntitlementTermDetails | null | undefined;
  isSearchable?: boolean;
  isFreePlanEntitlement?: boolean;
  shouldHideTermPayment?: boolean;
  isLinkedEntitlementsOnMonthlyPlan?: boolean;
  hasNoLinkedEntitlements?: boolean;
  allowDisableBasedOnCurrency?: boolean;
  allowDisableBasedOnPricingEligibility?: boolean;
  isDesignIteration1Enabled?: boolean;
  primaryCustomerContact?: {
    isLoading?: boolean;
    data?: EndCustomerContact;
  };
  shouldShowACHPaymentMethod?: boolean;
}

export const BillingProfileFormField = ({
  fieldName = 'invoiceGroupId',
  fieldLabel = 'Billing profile',
  isRequired = false,
  billingProfiles = [],
  entitlementBillingInvoiceGroupId = null,
  scheduledBillingInvoiceGroupId = null,
  isDisabled,
  setSelectedInvoiceGroupPricingPlanValue = null,
  productId,
  showTermPaymentMethodGroup = true,
  onChange,
  selected,
  className,
  billingFrequency,
  endTimeStamp = 0,
  creditDetails,
  isSearchable,
  isFreePlanEntitlement,
  shouldHideTermPayment,
  isLinkedEntitlementsOnMonthlyPlan,
  hasNoLinkedEntitlements,
  allowDisableBasedOnCurrency,
  allowDisableBasedOnPricingEligibility,
  isDesignIteration1Enabled,
  primaryCustomerContact,
  shouldShowACHPaymentMethod,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const selectedCurrency = selected?.currency;
  const isAnnualOrMonthlyEvergreen =
    (billingFrequency === BillingFrequency.ANNUAL || billingFrequency === BillingFrequency.MONTHLY) && !endTimeStamp;

  const getDisabledMessage = (invoiceGroup: InvoiceGroup, selected?: InvoiceGroup | null) => {
    const selectedPricingEligibility = startCase(
      defaultTo(get(selected, 'shipToPriceEligibility'), COMMERCIAL_PRICE_ELIGIBILITY)
    );
    const isDifferentPricingEligibility =
      allowDisableBasedOnPricingEligibility &&
      defaultTo(get(invoiceGroup, 'shipToPriceEligibility'), COMMERCIAL_PRICE_ELIGIBILITY).toLowerCase() !==
        selectedPricingEligibility.toLowerCase();
    const isDifferentCurrency =
      allowDisableBasedOnCurrency &&
      selectedCurrency &&
      invoiceGroup.currency?.toUpperCase() !== selectedCurrency.toUpperCase();

    if (isDifferentCurrency && isDifferentPricingEligibility) {
      return 'This billing profile is setup with different pricing eligibility and payment method that is not suitable for this transaction.';
    }

    if (isDifferentCurrency) {
      return 'This billing profile is setup with a payment method that is not suitable for this transaction.';
    }
    if (isDifferentPricingEligibility) {
      return 'This billing profile is setup with different pricing eligibility.';
    }
  };

  const formatLabelForSearchWithinDropDown = (invoiceGroup: InvoiceGroup) => {
    let formattedLabel = '';
    if (invoiceGroup?.name && invoiceGroup?.name?.toLowerCase() !== 'none') {
      formattedLabel += invoiceGroup.name;
    } else {
      formattedLabel += `Unnamed billing profile`;
    }
    if (invoiceGroup?.currency) {
      formattedLabel += ` (${invoiceGroup.currency})`;
    }
    if (invoiceGroup?.shipToParty) {
      formattedLabel += `, ${formatAddressLabel(invoiceGroup.shipToParty)}`;
    } else {
      formattedLabel += `, No sold-to address`;
    }
    if (invoiceGroup?.defaultPaymentMethod) {
      formattedLabel += `, ${formatCreditCardLabel(invoiceGroup.defaultPaymentMethod)}`;
    } else {
      formattedLabel += `, No payment method`;
    }
    return formattedLabel;
  };

  const shouldHidePaymentOnTerms =
    (!isDisabled && isAnnualOrMonthlyEvergreen && isCreditManagementEnabled && !isStatuspage(productId)) ||
    !showTermPaymentMethodGroup ||
    billingFrequency === BillingFrequency.MONTHLY ||
    (shouldHideTermPayment && isFreePlanEntitlement && hasNoLinkedEntitlements) ||
    (shouldHideTermPayment && isFreePlanEntitlement && isLinkedEntitlementsOnMonthlyPlan) ||
    (shouldHideTermPayment && billingFrequency === BillingFrequency?.ANNUAL && isLinkedEntitlementsOnMonthlyPlan);

  if (shouldHidePaymentOnTerms) {
    billingProfiles = shouldHideTermPayment
      ? // Filter out all term payments except for any current selected.
        billingProfiles?.filter(
          (invoiceGroup) =>
            invoiceGroup === selected || invoiceGroup?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED
        ) ?? []
      : billingProfiles?.filter(
          (invoiceGroup) => invoiceGroup?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED
        ) ?? [];
  }

  const invoiceGroupSelectItems = (billingProfiles ?? [])
    .map((invoiceGroup) => {
      return {
        invoiceGroup,
        creditDetails,
        label: formatLabelForSearchWithinDropDown(invoiceGroup),
        value: invoiceGroup.id,
        isDisabled: !!getDisabledMessage(invoiceGroup, selected),
        disabledMessage: getDisabledMessage(invoiceGroup, selected),
        selectedCurrency: selectedCurrency,
      } as BillingProfileSelectItem;
    })
    // sort the billing profiles disabled in bottom
    .sort((a, b) => +a.isDisabled - +b.isDisabled);

  const getDefaultBillingGroup = (invoiceGroupId: string) => {
    return invoiceGroupSelectItems.filter((invoiceGroupItem: BillingProfileSelectItem) => {
      return invoiceGroupItem.value === invoiceGroupId;
    });
  };

  const hasSingleResult = invoiceGroupSelectItems.length === 1;
  const defaultValue =
    selected && selected.id
      ? getDefaultBillingGroup(selected.id)[0]
      : scheduledBillingInvoiceGroupId
      ? getDefaultBillingGroup(scheduledBillingInvoiceGroupId)[0]
      : entitlementBillingInvoiceGroupId
      ? getDefaultBillingGroup(entitlementBillingInvoiceGroupId)[0]
      : hasSingleResult
      ? invoiceGroupSelectItems[0]
      : undefined;

  const renderBillingProfileCards = (
    billingProfiles: BillingProfileSelectItem[],
    fieldProps?: FieldProps<BillingProfileSelectItem, HTMLInputElement>
  ) => {
    return (
      <SwitchBillingProfileCards
        billingProfiles={billingProfiles}
        setSelectedInvoiceGroupPricingPlanValue={setSelectedInvoiceGroupPricingPlanValue}
        selected={selected}
        onChange={(selectedItem: BillingProfileSelectItem) => {
          if (onChange) {
            onChange(selectedItem);
          }
          // Call the fieldProps.onChange to update the form value which will be used in form validation
          fieldProps?.onChange(selectedItem);
        }}
        primaryCustomerContact={primaryCustomerContact}
        shouldShowACHPaymentMethod={shouldShowACHPaymentMethod}
      />
    );
  };

  const renderBillingProfileSection = () => {
    const availableBillingProfiles = invoiceGroupSelectItems?.filter(({ disabledMessage }) => !disabledMessage);

    return (
      <Field
        name={fieldName}
        label={fieldLabel}
        isRequired={isRequired}
        defaultValue={defaultValue}
        isDisabled={isDisabled}
      >
        {({ fieldProps }) =>
          isDesignIteration1Enabled ? (
            renderBillingProfileCards(availableBillingProfiles, fieldProps)
          ) : (
            <div data-testid="billing-profile-select-dropdown" className={className}>
              <Select<BillingProfileSelectItem>
                {...fieldProps}
                placeholder="Select billing profile"
                options={invoiceGroupSelectItems}
                components={{
                  Option: BillingProfileSelectOption,
                  //@ts-ignore
                  SingleValue: BillingProfileSelectOption,
                }}
                onChange={(selectedItem: any) => {
                  if (setSelectedInvoiceGroupPricingPlanValue) {
                    setSelectedInvoiceGroupPricingPlanValue(selectedItem.invoiceGroup.shipToPriceEligibility);
                  }
                  if (onChange) {
                    onChange(selectedItem);
                  }
                  fieldProps.onChange(selectedItem);
                }}
                isSearchable={isSearchable}
                inputId="IG-input-field"
              />
            </div>
          )
        }
      </Field>
    );
  };

  const renderNotAvailableBillingProfiles = () => {
    const notAvailableBillingProfiles = invoiceGroupSelectItems?.filter(({ disabledMessage }) => disabledMessage);
    if (isDesignIteration1Enabled && notAvailableBillingProfiles?.length > 0) {
      const getHeader = () => (
        <Box paddingInlineStart="space.400">
          <Flex gap="space.100" alignItems="center">
            {/* Above 500 it will show 500+ in badge */}
            <Badge max={500}>{notAvailableBillingProfiles?.length ?? '-'}</Badge>
            <Text color="color.text.subtle" weight="semibold">
              unavailable billing profiles
            </Text>
          </Flex>
        </Box>
      );

      return (
        <NotAvailableBillingProfiles>
          <CustomPanel
            testId="not-available-billing-profiles"
            expandedHeader={getHeader()}
            collapsedHeader={getHeader()}
            version={isDesignIteration1Enabled ? 3 : undefined}
            backgroundColor={N0}
            padding="space.200"
          >
            <Box paddingBlockStart="space.100">{renderBillingProfileCards(notAvailableBillingProfiles)}</Box>
          </CustomPanel>
        </NotAvailableBillingProfiles>
      );
    }
    return null;
  };

  return (
    <div>
      {renderBillingProfileSection()}
      {renderNotAvailableBillingProfiles()}
    </div>
  );
};
